import { FilterBar, Select, useTranslate } from '@athena/ui'

export interface DashboardFilterValue {
  studentId?: string | undefined
  practiceId?: string | undefined
}

export interface DashboardFiltersProps {
  value: DashboardFilterValue
  onChange: (next: DashboardFilterValue) => void
  students: ReadonlyArray<{ id: string; displayName: string }>
  practices: ReadonlyArray<{ id: string; title: string }>
}

/**
 * "No filter" needs a value of its own: Radix refuses an empty string as an
 * item value, and an id could in principle be anything, so this is a word no
 * uuid can collide with.
 */
const ANY = '__any__'

/**
 * Student and practice — the two questions a tutor asks of the same data.
 *
 * There was a third, a period of "all time / last 7 days / last 30 days / last
 * 3 months". It is gone: the dashboard is a list of what needs attention now,
 * and a tutor reading it does not first decide how far back "now" goes. The
 * server can still be given a `since` — `DashboardFilterInput` is unchanged —
 * but nothing in the product asks for one.
 *
 * The values live in the URL, so a filtered dashboard is a link that can be
 * kept or shared, exactly as the practice list's search already is. Nothing is
 * filtered here: the choices go to the server, which is the only place that
 * can narrow rows it is willing to return.
 */
export function DashboardFilters({ value, onChange, students, practices }: DashboardFiltersProps) {
  const t = useTranslate()
  const anyApplied = value.studentId !== undefined || value.practiceId !== undefined

  return (
    <FilterBar
      clearLabel={t('common.clearFilters')}
      {...(anyApplied ? { onClear: () => onChange({}) } : {})}
    >
      <Select
        label={t('dashboard.filters.student')}
        searchable
        visibleOptionCount={6}
        value={value.studentId ?? ANY}
        onValueChange={(next) => onChange({ ...value, studentId: next === ANY ? undefined : next })}
        options={[
          { value: ANY, label: t('dashboard.filters.everyStudent') },
          ...students.map((student) => ({ value: student.id, label: student.displayName })),
        ]}
      />

      <Select
        label={t('dashboard.filters.practice')}
        searchable
        visibleOptionCount={6}
        value={value.practiceId ?? ANY}
        onValueChange={(next) =>
          onChange({ ...value, practiceId: next === ANY ? undefined : next })
        }
        options={[
          { value: ANY, label: t('dashboard.filters.everyPractice') },
          ...practices.map((practice) => ({ value: practice.id, label: practice.title })),
        ]}
      />
    </FilterBar>
  )
}
